import React, { useState, useEffect, useRef } from 'react';
import { ScreensaverSettings, WeatherData, WeatherUnits } from '../../shared/types.js';
import { useTimeClock } from '../hooks/useTimeClock';

interface ScreensaverProps {
    settings: ScreensaverSettings;
}

const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        month: 'short',
        day: 'numeric',
        hour12: false,
    }).replace(',', '');
};

const getWeatherInfoFromCode = (code: number, isDay: boolean): { description: string; icon: string } => {
    const dayNight = isDay ? '_day' : '_night';
    switch (code) {
        case 0: return { description: "Clear Sky", icon: `clear${dayNight}` };
        case 1: return { description: "Mainly Clear", icon: `mostly_clear${dayNight}` };
        case 2: return { description: "Partly Cloudy", icon: `partly_cloudy${dayNight}` };
        case 3: return { description: "Overcast", icon: `cloudy` };
        case 45: return { description: "Fog", icon: `haze_fog_dust_smoke` };
        case 48: return { description: "Rime Fog", icon: `haze_fog_dust_smoke` };
        case 51: return { description: "Drizzle", icon: `scattered_showers${dayNight}` };
        case 53: return { description: "Drizzle", icon: `scattered_showers${dayNight}` };
        case 55: return { description: "Drizzle", icon: `scattered_showers${dayNight}` };
        case 56: return { description: "Light Drizzle Rain", icon: `mixed_rain_snow` };
        case 57: return { description: "Heavy Drizzle Rain", icon: `mixed_rain_snow` };
        case 61: return { description: "Light Rain", icon: `drizzle` };
        case 63: return { description: "Moderate Rain", icon: `showers_rain` };
        case 65: return { description: "Heavy Rain", icon: `heavy_rain` };
        case 66: return { description: "Light Freezing Rain", icon: `icy` };
        case 67: return { description: "Heavy Freezing Rain", icon: `icy` };
        case 71: return { description: "Light Snow Fall", icon: `flurries` };
        case 73: return { description: "Moderate Snow Fall", icon: `showers_snow` };
        case 75: return { description: "Heavy Snow Fall", icon: `heavy_snow` };
        case 77: return { description: "Light Snow Showers", icon: `scattered_snow_showers${dayNight}` };
        case 80: return { description: "Rain Showers", icon: `scattered_showers${dayNight}` };
        case 81: return { description: "Rain Showers", icon: `scattered_showers${dayNight}` };
        case 82: return { description: "Rain Showers", icon: `scattered_showers${dayNight}` };
        case 85: return { description: "Light Snow Showers", icon: `scattered_snow_showers${dayNight}` };
        case 86: return { description: "Heavy Snow Showers", icon: `scattered_snow_showers${dayNight}` };
        case 95: return { description: "Thunderstorm", icon: `isolated_scattered_thunderstorms${dayNight}` };
        case 96: return { description: "Light Thunderstorm Hail", icon: `strong_thunderstorms` };
        case 99: return { description: "Thunderstorm With Hail", icon: `strong_thunderstorms` };
        default: return { description: "Clear Sky", icon: `01${dayNight}` };
    }
};

const WeatherWidget: React.FC = () => {
    const { screensaverSettings } = useTimeClock();
    const [weather, setWeather] = useState<WeatherData | null>(null);
    const [weatherError, setWeatherError] = useState<string | null>(null);
    useEffect(() => {
        const fetchWeather = async () => {
            if (!screensaverSettings?.latitude || !screensaverSettings.longitude) {
                setWeather(null);
                setWeatherError("Weather location is not configured.");
                return;
            }
            const { latitude, longitude, weatherUnits } = screensaverSettings;
            const tempUnit = weatherUnits === WeatherUnits.METRIC ? 'celsius' : 'fahrenheit';
            const windUnit = weatherUnits === WeatherUnits.METRIC ? 'ms' : 'mph';
            const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,apparent_temperature,is_day,precipitation,weather_code,pressure_msl,wind_speed_10m&temperature_unit=${tempUnit}&wind_speed_unit=${windUnit}&precipitation_unit=mm`;
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.reason || 'Failed to fetch weather data');
                }
                const data = await response.json();
                const { description, icon } = getWeatherInfoFromCode(data.current.weather_code, data.current.is_day === 1);
                setWeather({
                    temp: Math.round(data.current.temperature_2m),
                    description: description,
                    icon: icon,
                    feelsLike: Math.round(data.current.apparent_temperature),
                    windSpeed: data.current.wind_speed_10m,
                    humidity: data.current.relative_humidity_2m,
                    pressure: data.current.pressure_msl,
                    precipitation: data.current.precipitation,
                    timestamp: new Date(data.current.time).getTime(),
                });
                setWeatherError(null);
            } catch (error: any) {
                console.error("Weather fetch error:", error);
                setWeatherError(error.message);
                setWeather(null);
            }
        };
        fetchWeather();
        const weatherInterval = setInterval(fetchWeather, 15 * 60 * 1000);
        return () => clearInterval(weatherInterval);
    }, [screensaverSettings]);
    if (weatherError && !weather) {
        return <div className="text-center text-red-400 p-4">{weatherError}</div>;
    }
    if (!weather) {
        return null;
    }
    const unitSymbol = screensaverSettings?.weatherUnits === 'metric' ? 'C' : 'F';
    const windSpeedUnit = screensaverSettings?.weatherUnits === 'metric' ? 'm/s' : 'mph';
    return (
        <div className="flex flex-col p-8 bg-black/50 rounded-3xl text-white font-sans backdrop-blur-sm">
          <div className="flex justify-between items-start">
            <h2 className="text-4xl font-bold capitalize">{weather.description}</h2>
            <img src={`/images/${weather.icon}.png`} alt={weather.description} className="w-24 h-24 -mt-4 -mr-4" />
          </div>
          <div className="flex items-end justify-between mt-4">
            <div className="flex items-start">
              <p className="text-9xl font-bold leading-none tracking-tighter">{weather.temp}</p>
              <span className="text-5xl font-light mt-2">°{unitSymbol}</span>
            </div>
            <div className="text-right text-xl grid grid-cols-2 gap-x-6 gap-y-1">
              <span className="font-light">Feels like</span><span className="font-bold">{weather.feelsLike}°</span>
              <span className="font-light">Wind</span><span className="font-bold">{weather.windSpeed.toFixed(1)} {windSpeedUnit}</span>
              <span className="font-light">Humidity</span><span className="font-bold">{weather.humidity}%</span>
              <span className="font-light">Precip</span><span className="font-bold">{weather.precipitation.toFixed(1)} mm</span>
              <span className="font-light">Pressure</span><span className="font-bold">{weather.pressure.toFixed(0)} hPa</span>
            </div>
          </div>
          <div className="flex justify-between items-center mt-6 pt-4 border-t border-white/20 text-sm">
            <div className="flex items-center gap-2">
                <span className="font-semibold"></span>
            </div>
            <span>Last Update: {formatTimestamp(weather.timestamp)}</span>
          </div>
        </div>
    );
};

const Screensaver: React.FC<ScreensaverProps> = ({ settings }) => {
    const [time, setTime] = useState(new Date());
    const [position, setPosition] = useState({ top: '50%', left: '50%' });
    const contentRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        const moveContent = () => {
            if (contentRef.current) {
                const contentWidth = contentRef.current.offsetWidth;
                const contentHeight = contentRef.current.offsetHeight;
                const screenWidth = window.innerWidth;
                const screenHeight = window.innerHeight;
                const margin = 20;
				const halfHeight = contentHeight / 2;
				const halfWidth = contentWidth / 2;
				const minTopPx = halfHeight + margin;
                const maxTopPx = screenHeight - halfHeight - margin;
                const minLeftPx = halfWidth + margin;
                const maxLeftPx = screenWidth - halfWidth - margin;
                if (maxTopPx < minTopPx || maxLeftPx < minLeftPx) {
                    setPosition({ top: '50%', left: '50%' });
                    return;
                }
                const newTopPx = Math.random() * (maxTopPx - minTopPx) + minTopPx;
                const newLeftPx = Math.random() * (maxLeftPx - minLeftPx) + minLeftPx;
                const newTopPercent = (newTopPx / screenHeight) * 100;
                const newLeftPercent = (newLeftPx / screenWidth) * 100;
                setPosition({ top: `${newTopPercent}%`, left: `${newLeftPercent}%` });
            }
        };
        const TEST_MODE = false;
		const initialMoveTimeout = setTimeout(moveContent, 100);
		const moveInterval = setInterval(moveContent, TEST_MODE ? 3000 : 60000);
        window.addEventListener('resize', moveContent);
        return () => {
            clearTimeout(initialMoveTimeout);
            clearInterval(moveInterval);
            window.removeEventListener('resize', moveContent);
        };
    }, []);
    useEffect(() => {
        const timeInterval = setInterval(() => {
            setTime(new Date());
        }, 1000);
        return () => clearInterval(timeInterval);
    }, []);
    const formattedTime = time.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true });
    const formattedDate = time.toLocaleDateString([], { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    return (
        <div className="fixed inset-0 bg-black z-[100] cursor-pointer">
            <div
                ref={contentRef}
                className="absolute text-white transition-all duration-1000 ease-in-out text-center"
                style={{ top: position.top, left: position.left, transform: 'translate(-50%, -50%)' }}
            >
                {settings.widgets.time && (
                    <h1 className="text-9xl font-mono font-bold whitespace-nowrap">{formattedTime}</h1>
                )}
                {settings.widgets.date && (
                    <p className="text-4xl mt-4">{formattedDate}</p>
                )}
                {settings.widgets.weather && (
                    <div className="mt-8">
                        <WeatherWidget />
                    </div>
                )}
            </div>
        </div>
    );
};

export default Screensaver;